import {useCallback, useEffect, useMemo, useRef, useState} from 'react';

import {useRampFlagMediaPanelReactFetch} from '../../../util/rampFeatureFlags/rampFeatureFlags';
import {
  type TileMedia,
  type TileMediaSpec,
  type TileMediaTracking,
} from '../../MediaCard';
import {type MediaBrowserPanelConfig} from '../common';
import {invalidateDirectURLCacheForFilePath} from './batching/directURLs';
import {invalidateAvailableFilesCacheForFilePath} from './batching/existingFilePaths';
import {fetchNewTileMedia} from './fetch';
import IN_MEMORY_CACHE, {
  invalidateTileMedia as invalidateTileMediaCache,
  type TileMediaStatus,
  updateTileMediaStatus,
} from './inMemoryCache';
import {getKeyForTileMediaSpec} from './keys';
import {useFileData} from './useFileData';

export * from './augment';
export {fetchMaxStepWithAvailableMedia} from './fetch';
export * from './keys';
export * from './paths';

export type Dimensions =
  | {
      height: number;
    }
  | {width: number}
  | {height: number; width: number};

/** Max times we invalidate and refetch on image load error to avoid retry loops when the URL always fails. */
const MAX_LOAD_ERROR_RETRIES = 2;

/**
 * Check for existing cached entry, which we only take if its step matches with the one specified in the spec.
 *
 * If the steps do not match, it means that we "rolled back" and displayed media from a previous step because we assumed that the media for the actual step hasn't finished uploading yet. Always refetch in case the existing entry is rolled back, as the real thing may be available now.
 */
function tileMediaSpecIsNew(tileMediaSpec: TileMediaSpec): boolean {
  const tileMediaSpecKey = getKeyForTileMediaSpec(tileMediaSpec);
  const existingStatus =
    IN_MEMORY_CACHE.TILE_MEDIA_STATUS_BY_KEY.get(tileMediaSpecKey);
  const existingTileMedia =
    IN_MEMORY_CACHE.TILE_MEDIA_BY_KEY.get(tileMediaSpecKey);

  // If we have data but the step doesn't match, we need to refetch
  if (existingTileMedia && existingTileMedia.step !== tileMediaSpec.mediaStep) {
    return true;
  }

  // If we've never requested this spec before, it's new
  if (!existingStatus || !existingStatus.isRequested) {
    return true;
  }

  if (existingStatus?.hasError) {
    return true;
  }

  // Otherwise, we've already requested this spec (successfully or with error), so it's not new
  return false;
}

export function isComplete(
  tileMediaStatus: TileMediaStatus | null | undefined
): boolean {
  return !!(
    tileMediaStatus &&
    (tileMediaStatus.isLoaded || tileMediaStatus.hasError)
  );
}

/**
 * Per-tile entry point for the Media Panel fetch pipeline.
 *
 * Resolves a `TileMediaSpec` to a `TileMedia` (path, URLs, blob) plus load
 * status and tracking. Behavior forks on `media_panel_react_fetch`:
 *
 * **Flag on** (`useFileData`, see `useFileData.ts`):
 * - Metadata: `useAvailableFilesQueryQuery` with `cache-first` (Apollo cache).
 * - Bytes: panel-scoped blob store via `useBlob` inside `useFileData`.
 * - Status and tracking come from hook render output; no global tile cache read.
 * - `useTileMediaStatusPoll` is disabled; Apollo/store subscriptions drive UI.
 *
 * **Flag off** (legacy, this file below the early return):
 * - `useEffect` calls `fetchNewTileMedia` when the spec is new.
 * - Results land in session-global `IN_MEMORY_CACHE` (`TILE_MEDIA_BY_KEY`, status maps).
 * - `useTileMediaStatusPoll` polls every 100ms until complete.
 * - Blob dedup uses `BLOB_PROMISE_BY_URL` in that same cache.
 *
 * Both paths share the same domain constraints (not removed by either fork):
 * - Shared assets across panels need explicit dedup (Apollo cache, batching, or panel store).
 * - File uploads lag history rows; react path tags `media_upload_pending` when
 *   `sizeBytes === 0` (still surfaces as not-found).
 * - Images may use Gorilla `url` instead of signed `directURL` for sizing (`shouldSkipBlobFetch`).
 * - Most types load through blob + `objectURL`; HLS and internal-URL images are exceptions.
 */
export function useTileMedia(
  tileMediaSpec: TileMediaSpec,
  attemptNonexistentSteps: boolean,
  config: Pick<MediaBrowserPanelConfig, 'disableVideoStream'>,
  skip?: boolean
): {
  loading: boolean;
  tileMedia: TileMedia | null;
  status: TileMediaStatus;
  getTileMediaTracking: (
    tileMediaSpec: TileMediaSpec
  ) => TileMediaTracking | null;
  invalidateAndRefetch: () => void;
} {
  // TODO(react-compiler): reads a mutable module cache (IN_MEMORY_CACHE) during
  // render; compiled output would cache it.
  'use no memo';
  const shouldUseReactFetch = useRampFlagMediaPanelReactFetch() ?? false;
  const disableVideoStream = config.disableVideoStream ?? false;
  const fileData = useFileData(tileMediaSpec, {
    skip: !shouldUseReactFetch || Boolean(skip),
    disableVideoStream,
  });
  const [invalidateVersion, setInvalidateVersion] = useState(0);
  const tileMediaSpecKey = useMemo(
    () => getKeyForTileMediaSpec(tileMediaSpec),
    [tileMediaSpec]
  );
  const attemptNonexistentStepsRef = useRef(attemptNonexistentSteps);
  const loadErrorRetryCountRef = useRef(0);

  useEffect(() => {
    attemptNonexistentStepsRef.current = attemptNonexistentSteps;
  }, [attemptNonexistentSteps]);

  useEffect(() => {
    loadErrorRetryCountRef.current = 0;
  }, [tileMediaSpecKey]);

  const setStatus = useCallback(
    (newStatus: Partial<TileMediaStatus>) => {
      updateTileMediaStatus(tileMediaSpecKey, newStatus);
    },
    [tileMediaSpecKey]
  );

  const getTileMediaTracking = useCallback((tileMediaSpec: TileMediaSpec) => {
    const tileMediaSpecKey = getKeyForTileMediaSpec(tileMediaSpec);
    return (
      IN_MEMORY_CACHE.TILE_MEDIA_TRACKING_BY_KEY.get(tileMediaSpecKey) ?? null
    );
  }, []);

  const invalidateAndRefetch = useCallback(() => {
    if (loadErrorRetryCountRef.current >= MAX_LOAD_ERROR_RETRIES) {
      return;
    }
    loadErrorRetryCountRef.current += 1;
    const tileMedia = IN_MEMORY_CACHE.TILE_MEDIA_BY_KEY.get(tileMediaSpecKey);
    if (tileMedia?.filePath != null) {
      invalidateDirectURLCacheForFilePath(
        tileMediaSpec.runSignature,
        tileMedia.filePath
      );
      invalidateAvailableFilesCacheForFilePath(
        tileMediaSpec.runSignature,
        tileMedia.filePath
      );
    }
    invalidateTileMediaCache(tileMediaSpecKey);
    setInvalidateVersion(v => v + 1);
  }, [tileMediaSpecKey, tileMediaSpec.runSignature]);

  const fetchData = useCallback(
    async (spec: TileMediaSpec) => {
      const status =
        IN_MEMORY_CACHE.TILE_MEDIA_STATUS_BY_KEY.get(tileMediaSpecKey);

      if (status?.isLoading || status?.isLoaded || skip) {
        return;
      }
      setStatus({
        isLoading: true,
        isLoaded: false,
        isRequested: true,
        lastRequestedAt: Date.now(),
      });

      try {
        await fetchNewTileMedia(
          [spec],
          attemptNonexistentStepsRef.current,
          config.disableVideoStream ?? false
        );
      } catch (error) {
        console.error('fetchData error', error);
      }
    },
    [tileMediaSpecKey, setStatus, skip, config.disableVideoStream]
  );

  useEffect(() => {
    if (shouldUseReactFetch) {
      return;
    }
    if (!skip && tileMediaSpecIsNew(tileMediaSpec)) {
      fetchData(tileMediaSpec);
    }
  }, [tileMediaSpec, fetchData, skip, invalidateVersion, shouldUseReactFetch]);

  const cachedGetTileMediaTracking = useCallback(
    (spec: TileMediaSpec) => {
      if (shouldUseReactFetch) {
        return fileData.tracking;
      }
      return getTileMediaTracking(spec);
    },
    [fileData.tracking, getTileMediaTracking, shouldUseReactFetch]
  );

  if (shouldUseReactFetch) {
    return {
      loading: !isComplete(fileData.status),
      tileMedia: fileData.tileMedia,
      status: fileData.status,
      getTileMediaTracking: cachedGetTileMediaTracking,
      invalidateAndRefetch: fileData.invalidateAndRefetch,
    };
  }

  const tileMedia =
    IN_MEMORY_CACHE.TILE_MEDIA_BY_KEY.get(tileMediaSpecKey) ?? null;
  const status = IN_MEMORY_CACHE.TILE_MEDIA_STATUS_BY_KEY.get(
    tileMediaSpecKey
  ) ?? {
    isLoading: false,
    isRequested: false,
    isLoaded: false,
    hasError: false,
    lastRequestedAt: 0,
    lastUpdatedAt: 0,
  };

  return {
    loading: !isComplete(status),
    tileMedia,
    status,
    getTileMediaTracking: cachedGetTileMediaTracking,
    invalidateAndRefetch,
  };
}
